import { createSelector } from "@reduxjs/toolkit";

import { localeOptionsSelector } from "../../../l10n/useLocaleOptions.ts";
import type { ValueSubtypeDefinition } from "../../valueSubtypeDefinition.ts";
import type { StringValueSubtypeSpec } from "../../valueSubtypeSpecs.ts";
import { LocaleComparisonView } from "./LocaleComparisonView.tsx";
import { localeOperators } from "./localeOperators.ts";
import { LOCALE_SUBTYPE_ID, LocaleSubtype, requireLocale } from "./localeSubtype.ts";

/**
 * The behaviour behind the locale subtype: which operators it offers, whether a
 * literal is a locale at all, and the form that edits a comparison over it.
 *
 * Core's own, and so in `coreValueSubtypeDefinitions` rather than contributed —
 * `locale` is core's external value, and an app that forgot to wire it would get
 * a warning and a silent fall back to plain-string comparison.
 */
export const localeValueSubtypeDefinition: ValueSubtypeDefinition<LocaleSubtype, StringValueSubtypeSpec> = {
  id: LOCALE_SUBTYPE_ID,
  type: "string",
  schema: LocaleSubtype,
  require: () => requireLocale(),
  operators: localeOperators,
  // The language list is the module's content languages, which are state.
  specSelector: createSelector([localeOptionsSelector], (options): StringValueSubtypeSpec => ({ options })),
  // Stateless, and knowingly so: a code stays valid after its content language is
  // removed, and simply stops matching. The picker only ever offers current
  // languages, so a comparison cannot be authored wrong — only stranded.
  isValid: (value) => typeof value === "string",
  // The same list the picker offers, so form and summary cannot drift. A code
  // whose language has gone reads as itself.
  formatLiteral: (value, _subtype, spec) =>
    spec?.options?.find((option) => option.value === value)?.label ?? String(value),
  views: { comparison: LocaleComparisonView },
};
